/* eslint-disable @typescript-eslint/no-confusing-void-expression */

import noop from 'lodash/noop';
import { subscribeToChannels } from '#/websocket/useWsInstance';
import {
  WEBSOCKET_RFQ_MESSAGE,
  track,
} from '@paradigm/analytics/src/analytics';
import { Grfq, GrfqTrade } from '#/unified-rfqs/entities/grfq/domain';
import {
  createGrfqWebSocket,
  GrfqNotification,
  GrfqNotificationByChannel,
  GrfqWebSocket,
  getGrfqChannels,
  GRFQ_TOPIC_NAME,
  NotificationErrorMap,
} from '#/unified-rfqs/repositories/grfq-ws';
import GrfqQuotesStore from '#/unified-rfqs/store/grfq-quotes';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import { parseQtyToClearingUnities } from '#/unified-rfqs/ui/rfqs/quotes-functions';
import OrdersStore from '#/unified-rfqs/store/orders';
import TradesStore from '#/unified-rfqs/store/trades';
import TradeTapeStore from '#/unified-rfqs/store/trade-tape';
import {
  WebSocketRfqService,
  WebSocketStatusHandler,
  WebSocketHandlersArgs,
} from '#/unified-rfqs/services/shared';

type GrfqNotifHandler = (
  rfq: Grfq,
  handler: (rfq: Partial<Grfq>) => void,
) => void;

export interface CallbackHandlers extends WebSocketHandlersArgs<GrfqTrade> {
  readonly handleRfqUpdated: GrfqNotifHandler;
  readonly handleRfqReceived: GrfqNotifHandler;
  readonly storeUserTrade: (rfqId: Grfq['id']) => void;
}

export interface WebSocketStatus {
  readonly error: string;
  readonly connected: boolean;
  readonly subscribed: boolean;
  readonly subscribing: boolean;
}

interface GrfqServiceDeps {
  rfqStore: RfqsStore;
  tradesStore: TradesStore;
  tradeTapeStore: TradeTapeStore;
  ordersStore: OrdersStore;
  grfqQuoteStore: GrfqQuotesStore;
  handlers: CallbackHandlers;
}

type GrfqWsType = GrfqWebSocket | null;

export class GrfqWsService implements WebSocketRfqService<GrfqWsType> {
  public ws: GrfqWsType = null;
  private abortController: AbortController | null = null;

  constructor(private deps: GrfqServiceDeps) {}

  startWebSocket() {
    if (this.ws != null) return noop;

    this.ws = createGrfqWebSocket();
    this.abortController?.abort();
    this.abortController = new AbortController();
    const unsubscribe = this.setupHandlers(this.ws);
    return () => {
      unsubscribe();
    };
  }

  close() {
    this.ws?.close();
    this.abortController?.abort();
    this.ws = null;
  }

  subscribeWebSocketStatus(handler: WebSocketStatusHandler) {
    const { ws } = this;
    if (ws == null) return noop;

    const handleOpen = () => {
      this.abortController?.abort();
      this.abortController = new AbortController();

      handler({
        error: '',
        connected: true,
        subscribing: true,
        subscribed: false,
      });

      this.subscribeChannels()
        .then((didSucceed) =>
          handler({
            error: didSucceed ? '' : "Couldn't subscribe to Paradigm channels",
            connected: true,
            subscribing: false,
            subscribed: didSucceed,
          }),
        )
        .catch((err: Error) =>
          handler({
            error: err.message,
            connected: false,
            subscribing: false,
            subscribed: false,
          }),
        );
    };
    ws.addEventListener('open', handleOpen);

    const handleClose = () => {
      handler({
        error: 'Disconnected from Paradigm servers. Trying to reconnect...',
        connected: false,
        subscribing: false,
        subscribed: false,
      });
      this.abortController?.abort();
    };
    ws.addEventListener('close', handleClose);

    const resetStores = () => {
      const { rfqStore, tradesStore, grfqQuoteStore, ordersStore } = this.deps;

      rfqStore.clear();
      tradesStore.clear();
      ordersStore.clear();
      grfqQuoteStore.clear();
    };
    ws.addEventListener('close', resetStores);
    ws.addEventListener('error', resetStores);

    const handleError = () => {
      handler({
        error: 'Error connecting to Paradigm servers',
        connected: false,
        subscribing: false,
        subscribed: false,
      });
    };
    ws.addEventListener('error', handleError);

    const teardown = () => {
      ws.removeEventListener('close', resetStores);
      ws.removeEventListener('error', resetStores);
      ws.removeEventListener('open', handleOpen);
      ws.removeEventListener('close', handleClose);
      ws.removeEventListener('error', handleError);
    };

    return teardown;
  }

  private async subscribeChannels() {
    if (this.ws == null) return false;
    const subs = await subscribeToChannels(
      this.ws,
      getGrfqChannels(),
      GRFQ_TOPIC_NAME,
      this.abortController?.signal,
    );
    const allChannelsSubscribed = subs.every(([, subscribed]) => subscribed);
    return allChannelsSubscribed;
  }

  private setupHandlers(ws: GrfqWebSocket) {
    const handler = (notification: GrfqNotification) => {
      this.handleErrorNotif(notification.errors);
      switch (notification.channel) {
        case 'quote':
          this.handleQuoteNotif(notification);
          break;
        case 'quote_book':
          this.handleQuoteBookNotif(notification);
          break;
        case 'quote_status':
          this.handleQuoteStatusNotif(notification);
          break;
        case 'rfq':
          this.handleRfqNotif(notification);
          break;
        case 'trade':
          this.handleTradeNotif(notification);
          break;
        case 'trade_tape':
          this.handleTradeTapeNotif(notification);
          break;
        case 'order':
          this.handleOrderNotif(notification);
          break;
        case 'market_maker_protection': // mmp is handled globally
          break;
        // no default
      }
    };

    return ws.addNotificationHandler(handler);
  }

  private handleRfqNotif(notification: GrfqNotificationByChannel['rfq']) {
    const { kind, rfq } = notification.data;
    const { rfqStore, handlers } = this.deps;

    track(WEBSOCKET_RFQ_MESSAGE, { kind, rfq });
    console.info(`Received RFQ notification kind ${kind}:`, rfq);

    switch (kind) {
      case 'REMOVED':
        rfqStore.removeOne(rfq.id);
        break;
      case 'ADDED': {
        // doesn't get notified when a rfq is recreated by the user
        handlers.handleRfqReceived(rfq, () => rfqStore.upsertMany([rfq]));
        break;
      }
      case 'UPDATED':
        handlers.handleRfqUpdated(rfq, (grfq: Partial<Grfq>) =>
          rfqStore.updateOne(rfq.id, grfq),
        );
        break;
      // no default
    }
  }

  private handleQuoteNotif(notification: GrfqNotificationByChannel['quote']) {
    const { data } = notification;
    const { grfqQuoteStore, ordersStore } = this.deps;

    switch (data.kind) {
      case 'NEW':
      case 'PARTIALLY_FILLED':
      case 'PARTIALLY_CANCELED':
        ordersStore.upsertMany([data.quote]);
        grfqQuoteStore.upsertQuote(data.quote);
        break;
      case 'CANCELED':
      case 'FILLED':
        ordersStore.updateClosedOrder(data.quote);
        grfqQuoteStore.removeQuote(data.quote);
        break;
      case 'PENDING_FILL':
        break;
      // no default
    }
  }

  private handleQuoteBookNotif(
    notification: GrfqNotificationByChannel['quote_book'],
  ) {
    const { data } = notification;
    const { grfqQuoteStore, rfqStore } = this.deps;
    const rfq = rfqStore.selectById(data.quote.rfq_id);
    const description = rfq?.description ?? '';
    const productCodes = rfq?.type === 'grfq' ? rfq.product_codes : [];
    const quote = {
      ...data.quote,
      description,
      product_codes: productCodes as string[],
    };

    switch (data.kind) {
      case 'ADDED':
      case 'UPDATED':
        grfqQuoteStore.upsertQuote(quote);
        break;
      case 'REMOVED':
        grfqQuoteStore.removeQuote(quote);
        break;
      // no default
    }
  }

  private handleTradeNotif(notification: GrfqNotificationByChannel['trade']) {
    const { data } = notification;
    const { handlers, rfqStore, tradesStore } = this.deps;
    const getParsedQty = () => {
      const { qty } = parseQtyToClearingUnities(
        data.trade.quantity,
        data.trade.product_codes[0]!,
      );
      return qty;
    };

    switch (data.kind) {
      case 'REJECTION':
        rfqStore.updateOne(data.trade.rfq_id, { status: 'ACTIVE' });
        tradesStore.handleTrade(data.trade);
        if (data.trade.rejected_reason != null)
          return handlers.onError(
            data.trade.rejected_reason,
            undefined,
            'trade',
          );
        break;
      case 'CONFIRMATION':
        tradesStore.handleTrade(data.trade);

        handlers.onTradeCompleted({
          ...data.trade,
          quantity: getParsedQty(),
        });
        handlers.storeUserTrade(data.trade.rfq_id);
        break;
      // no default
    }
  }

  private handleOrderNotif(notification: GrfqNotificationByChannel['order']) {
    const { data } = notification;
    const { ordersStore } = this.deps;

    switch (data.kind) {
      case 'SUMMARY':
        ordersStore.upsertMany([data.order.quote]);
        break;
      case 'PENDING_FILL':
      default:
        break;
    }
  }

  private handleQuoteStatusNotif(
    notification: GrfqNotificationByChannel['quote_status'],
  ) {
    const { onError } = this.deps.handlers;
    const { error_message, error_details, success } = notification.data;

    // Note: API specifies error_details as a string, but it can be an object
    let errorDetailsString: string | undefined;
    try {
      errorDetailsString =
        typeof error_details === 'string'
          ? error_details
          : JSON.stringify(error_details);
    } catch (error) {
      errorDetailsString = undefined;
    }

    const hasError =
      !success && error_message !== undefined && error_message !== '';

    if (hasError) onError(error_message, errorDetailsString);
  }

  private handleTradeTapeNotif(
    notification: GrfqNotificationByChannel['trade_tape'],
  ) {
    const { data } = notification;
    const { tradeTapeStore } = this.deps;

    switch (data.kind) {
      case 'COMPLETED':
        tradeTapeStore.handleTrade(data.trade);
        break;
      // no default
    }
  }

  private handleErrorNotif(errors: NotificationErrorMap) {
    const { onError } = this.deps.handlers;
    // toast notification errors
    Object.values(errors).forEach((error) => error != null && onError(error));
  }
}

export function createGrfqWsService(deps: GrfqServiceDeps): GrfqWsService {
  return new GrfqWsService({
    ...deps,
  });
}
